import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { obterHistoricoKit } from "../services/kitService";

interface HistoricoItem {
  id: number;
  dataAlteracao: Date;
  usuario: string;
  acao: string;
  descricao: string;
}

interface ModalHistoricoProps {
  open: boolean;
  onClose: () => void;
  idKit?: number;
}

export const ModalHistorico: React.FC<ModalHistoricoProps> = ({
  open,
  onClose,
  idKit,
}) => {
  const [loading, setLoading] = useState(true);
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);

  useEffect(() => {
    if (open && idKit) {
      carregarHistorico();
    }
  }, [open, idKit]);

  const carregarHistorico = async () => {
    setLoading(true);
    try {
      const data = await obterHistoricoKit(idKit!);
      setHistorico(data || []);
    } catch (error) {
      console.error("Erro ao carregar histórico:", error);
      setHistorico([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxWidth: 900 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ color: "#4a4a4a" }}>
            Log de Atualizações - Kit
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "#3C90AD" }} />
          </Box>
        ) : historico.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">
              Nenhum histórico de alteração encontrado.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: "1px solid #e0e0e0" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600, width: 150 }}>Data/Hora</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 150 }}>Usuário</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 150 }}>Ação</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descrição</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historico.map((item, index) => (
                  <TableRow
                    key={item.id || index}
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fafafa" } }}
                  >
                    <TableCell>
                      {item.dataAlteracao
                        ? format(new Date(item.dataAlteracao), "dd/MM/yyyy HH:mm", {
                            locale: ptBR,
                          })
                        : "-"}
                    </TableCell>
                    <TableCell>{item.usuario || "-"}</TableCell>
                    <TableCell>{item.acao || "-"}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={item.descricao}
                    >
                      {item.descricao || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};
